import {
  ProductVariantSneakersResponse,
  SizePickerOption,
  SizePickerVariant,
} from 'productTemplate/types/ProductVariant'
import { formatMoneyObject } from 'shared/lib/formatMoneyObject'
import { Price, PriceLocalized } from 'shared/types'
import { ISize } from 'shared/types/ISize'
import { BoxCondition } from './types/BoxCondition'

export type SizePickerVariantOption = {
  size: ISize
  new?: ProductVariantSneakersResponse
  used?: ProductVariantSneakersResponse
}

export const isBadBox = (boxCondition?: BoxCondition) => {
  return boxCondition !== 'good_condition'
}

export const isConditional = (selectedSize?: SizePickerOption) => {
  if (!selectedSize || !selectedSize.new) {
    return false
  }

  return (
    selectedSize.new?.shoeCondition === 'new_with_defects' ||
    isBadBox(selectedSize.new?.boxCondition)
  )
}

export const getLowestPrice = (variant?: ProductVariantSneakersResponse, fallback = Infinity) => {
  return variant?.lowestPriceCents?.amount ?? fallback
}
export const getInstantShipPrice = (
  variant?: ProductVariantSneakersResponse,
  fallback = Infinity,
) => {
  return variant?.instantShipLowestPriceCents?.amount ?? fallback
}
export const getMarketPrice = (variant?: ProductVariantSneakersResponse, fallback = Infinity) =>
  Math.min(getLowestPrice(variant, fallback), getInstantShipPrice(variant, fallback))

const getHighestMarketPrice = (variant?: ProductVariantSneakersResponse, fallback = Infinity) =>
  Math.max(getLowestPrice(variant, fallback), getInstantShipPrice(variant, fallback))

export const getLowestPricedVariant = (
  variant1?: ProductVariantSneakersResponse,
  variant2?: ProductVariantSneakersResponse,
) => (getInstantShipPrice(variant2) < getInstantShipPrice(variant1) ? variant2 : variant1)

export const getLowestMarketPricedVariant = (
  variant1?: ProductVariantSneakersResponse,
  variant2?: ProductVariantSneakersResponse,
) => (getMarketPrice(variant2) < getMarketPrice(variant1) ? variant2 : variant1)

export const getHighestPricedVariant = (
  variant1?: ProductVariantSneakersResponse,
  variant2?: ProductVariantSneakersResponse,
) => (getInstantShipPrice(variant1, 0) > getInstantShipPrice(variant2, 0) ? variant1 : variant2)

export const getHighestMarketPricedVariant = (
  variant1?: ProductVariantSneakersResponse,
  variant2?: ProductVariantSneakersResponse,
) => (getHighestMarketPrice(variant1, 0) > getHighestMarketPrice(variant2, 0) ? variant1 : variant2)

/**
 * Transforms a PriceLocalized object from server responses
 * into a Price object more suitable for display.
 */
export const resolveVariantPrice = (
  price: PriceLocalized | null,
  disambiguatedCurrencySymbol?: string,
): Price | null => {
  if (!price || !price.amount) {
    return null
  }

  return {
    display: formatMoneyObject(price, {
      useGrouping: true,
      showCents: false,
      disambiguatedCurrencySymbol,
    }),
    localizedValue: price.amount,
    value: price.amountUsdCents,
  }
}

/**
 * Get the price from a product variant, considering market pricing FF.
 */
export const getPriceFromVariant = (
  variant?: ProductVariantSneakersResponse,
  marketPricingEnabled?: boolean,
): PriceLocalized | null => {
  if (!variant) {
    return null
  }
  if (!marketPricingEnabled && !variant.instantShipLowestPriceCents.amount) {
    return null
  }
  if (!marketPricingEnabled) {
    return variant.instantShipLowestPriceCents
  }
  return getInstantShipPrice(variant) < getLowestPrice(variant)
    ? variant.instantShipLowestPriceCents
    : variant.lowestPriceCents
}

export const createPriceFromVariant = (
  variant?: ProductVariantSneakersResponse,
  marketPricingEnabled?: boolean,
) => {
  if (!variant) {
    return null
  }
  if (!marketPricingEnabled && !variant.instantShipLowestPriceCents.amount) {
    return null
  }
  if (!marketPricingEnabled) {
    return {
      value: variant.instantShipLowestPriceCents.amountUsdCents,
      localizedValue: variant.instantShipLowestPriceCents.amount,
    }
  }
  return getInstantShipPrice(variant) < getLowestPrice(variant)
    ? {
        value: variant.instantShipLowestPriceCents.amountUsdCents,
        localizedValue: variant.instantShipLowestPriceCents.amount,
      }
    : {
        value: variant.lowestPriceCents.amountUsdCents,
        localizedValue: variant.lowestPriceCents.amount,
      }
}

const convertProductVariantPricing = (
  variant: ProductVariantSneakersResponse,
  marketPricingEnabled: boolean,
  disambiguatedCurrencySymbol?: string,
): SizePickerVariant => ({
  boxCondition: variant.boxCondition,
  instantShipLowestPriceCents: resolveVariantPrice(
    variant.instantShipLowestPriceCents,
    disambiguatedCurrencySymbol,
  ),
  lowestPriceCents:
    marketPricingEnabled && variant.lowestPriceCents.amount
      ? resolveVariantPrice(variant.lowestPriceCents, disambiguatedCurrencySymbol)
      : null,
  shoeCondition: variant.shoeCondition,
  size: {
    display: variant.sizeOption.presentation,
    value: variant.sizeOption.value,
  },
})

export const convertSizePickerVariantPricing = (
  option: SizePickerVariantOption,
  marketPricingEnabled: boolean,
  disambiguatedCurrencySymbol?: string,
): SizePickerOption => ({
  new:
    option.new &&
    convertProductVariantPricing(option.new, marketPricingEnabled, disambiguatedCurrencySymbol),
  size: option.size,
  used:
    option.used &&
    convertProductVariantPricing(option.used, marketPricingEnabled, disambiguatedCurrencySymbol),
})
